import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  X,
  Loader2,
  Building2,
  Phone,
  Upload,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import { Pagination } from "@/components/pagination";
import { SearchableSelect } from "@/components/searchable-select";

interface Client {
  id: number;
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  tax_number: string;
  city: string;
  district: string;
  sector: string;
  notes: string;
  client_type?: string;
  logo_r2_key?: string | null;
  project_count?: number;
  created_at: string;
}

interface ClientsResponse {
  data: Client[];
  pagination: {
    page: number;
    totalPages: number;
    total: number;
  };
}

interface Il {
  id: number;
  name: string;
  bolge: number;
}

interface Ilce {
  id: number;
  name: string;
}

const SECTORS = [
  "İmalat",
  "Tarım ve Hayvancılık",
  "Gıda",
  "Tekstil",
  "Madencilik",
  "Enerji",
  "Turizm ve Otelcilik",
  "İnşaat",
  "Sağlık",
  "Eğitim",
  "Lojistik ve Taşımacılık",
  "Bilişim ve Teknoloji",
  "Kimya",
  "Otomotiv",
  "Savunma Sanayii",
  "Diğer",
];

const CLIENT_TYPES = [
  { value: "potential", label: "Potansiyel" },
  { value: "active", label: "Aktif" },
];

const initialForm = {
  company_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  tax_number: "",
  city: "",
  district: "",
  sector: "",
  notes: "",
  client_type: "potential",
};

const ACCEPTED_IMAGE_TYPES = ".jpg,.jpeg,.png,.svg,.webp";

function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, "");
  // Remove leading 0 or 90
  let clean = digits;
  if (clean.startsWith("90") && clean.length > 2) clean = clean.slice(2);
  else if (clean.startsWith("0")) clean = clean.slice(1);

  if (clean.length === 0) return "";
  if (clean.length <= 3) return `(${clean}`;
  if (clean.length <= 6) return `(${clean.slice(0, 3)}) ${clean.slice(3)}`;
  if (clean.length <= 8) return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)} ${clean.slice(6)}`;
  return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)} ${clean.slice(6, 8)} ${clean.slice(8, 10)}`;
}

function phoneToRaw(formatted: string): string {
  const digits = formatted.replace(/\D/g, "");
  if (digits.length === 10) return `0${digits}`;
  return digits;
}

export function ClientsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Logo & Stamp
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [stampFile, setStampFile] = useState<File | null>(null);
  const [stampPreview, setStampPreview] = useState<string | null>(null);

  // İl/İlçe state
  const [iller, setIller] = useState<Il[]>([]);
  const [ilceler, setIlceler] = useState<Ilce[]>([]);
  const [selectedIlId, setSelectedIlId] = useState<number>(0);

  const navigate = useNavigate();

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("limit", "20");
  if (search) queryParams.set("q", search);
  if (typeFilter) queryParams.set("client_type", typeFilter);

  const { data, loading, error, refetch } = useFetch<ClientsResponse>(
    `/clients?${queryParams.toString()}`,
    [page, search, typeFilter]
  );

  // İlleri yükle
  useEffect(() => {
    if (!showModal) return;
    const API_BASE = import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/api`
      : "/api";
    fetch(`${API_BASE}/il`)
      .then((r) => r.json())
      .then((json) => {
        const items = Array.isArray(json.data) ? json.data : [];
        setIller(items.sort((a: Il, b: Il) => a.name.localeCompare(b.name, "tr")));
      })
      .catch(() => {});
  }, [showModal]);

  // İlçeleri yükle
  useEffect(() => {
    if (!selectedIlId) {
      setIlceler([]);
      return;
    }
    const API_BASE = import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/api`
      : "/api";
    fetch(`${API_BASE}/il/${selectedIlId}/ilce`)
      .then((r) => r.json())
      .then((json) => {
        const items = Array.isArray(json.data) ? json.data : [];
        setIlceler(items.sort((a: Ilce, b: Ilce) => a.name.localeCompare(b.name, "tr")));
      })
      .catch(() => {});
  }, [selectedIlId]);

  function handleFormChange(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleCityChange(ilId: number) {
    setSelectedIlId(ilId);
    const il = iller.find((i) => i.id === ilId);
    setForm((prev) => ({ ...prev, city: il?.name || "", district: "" }));
  }

  function handleDistrictChange(ilceId: number) {
    const ilce = ilceler.find((i) => i.id === ilceId);
    setForm((prev) => ({ ...prev, district: ilce?.name || "" }));
  }

  function handlePhoneChange(value: string) {
    const formatted = formatPhoneInput(value);
    setPhoneDisplay(formatted);
    setForm((prev) => ({ ...prev, contact_phone: phoneToRaw(formatted) }));
  }

  function handleFileSelect(file: File | null, type: "logo" | "stamp") {
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (type === "logo") {
      setLogoFile(file);
      setLogoPreview(url);
    } else {
      setStampFile(file);
      setStampPreview(url);
    }
  }

  function clearFile(type: "logo" | "stamp") {
    if (type === "logo") {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      setLogoFile(null);
      setLogoPreview(null);
    } else {
      if (stampPreview) URL.revokeObjectURL(stampPreview);
      setStampFile(null);
      setStampPreview(null);
    }
  }

  function openModal() {
    setForm(initialForm);
    setPhoneDisplay("");
    setSelectedIlId(0);
    setIlceler([]);
    clearFile("logo");
    clearFile("stamp");
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_name.trim()) return;
    setSubmitting(true);
    try {
      await adminFetch("/clients", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setForm(initialForm);
      setPhoneDisplay("");
      setShowModal(false);
      refetch();
    } catch {
      // Error handled
    } finally {
      setSubmitting(false);
    }
  }

  const ADMIN_API_BASE = import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/api/admin`
    : "/api/admin";

  const inputClass =
    "h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Firma adı ara..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="h-9 w-full rounded-lg border border-border bg-secondary pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent sm:w-72"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="h-9 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">Tüm Türler</option>
            {CLIENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={openModal}
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
        >
          <Plus className="h-4 w-4" />
          Yeni Müşteri
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {loading ? (
          <PageLoading />
        ) : error ? (
          <ErrorMessage message={error} onRetry={refetch} className="m-4" />
        ) : !data || data.data.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            Henüz müşteri bulunmuyor.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">Firma Adı</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">İlgili Kişi</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground sm:table-cell">Tür</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground md:table-cell">Telefon</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground lg:table-cell">Şehir</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground sm:table-cell">Proje</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground sm:table-cell">Tarih</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.data.map((client) => (
                    <tr
                      key={client.id}
                      onClick={() => navigate(`/clients/${client.id}`)}
                      className="cursor-pointer transition-colors hover:bg-secondary/50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {client.logo_r2_key ? (
                            <img
                              src={`${ADMIN_API_BASE}/clients/${client.id}/logo`}
                              alt=""
                              className="hidden h-6 w-6 shrink-0 rounded-full object-cover sm:block"
                            />
                          ) : (
                            <Building2 className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
                          )}
                          <span className="text-sm font-medium text-foreground">{client.company_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{client.contact_name}</td>
                      <td className="hidden px-4 py-3 sm:table-cell">
                        <span className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                          client.client_type === "active"
                            ? "bg-green-500/10 text-green-400 border border-green-500/20"
                            : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                        )}>
                          {client.client_type === "active" ? "Aktif" : "Potansiyel"}
                        </span>
                      </td>
                      <td className="hidden px-4 py-3 text-sm text-muted-foreground md:table-cell">{client.contact_phone || "-"}</td>
                      <td className="hidden px-4 py-3 text-sm text-muted-foreground lg:table-cell">{client.city || "-"}</td>
                      <td className="hidden px-4 py-3 text-sm text-muted-foreground sm:table-cell">{client.project_count ?? 0}</td>
                      <td className="hidden px-4 py-3 text-sm text-muted-foreground sm:table-cell">{formatDate(client.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={data.pagination.page} totalPages={data.pagination.totalPages} onPageChange={setPage} />
          </>
        )}
      </div>

      {/* New Client Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowModal(false)} />
          <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Yeni Müşteri</h2>
              <button onClick={() => setShowModal(false)} className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">Firma Adı *</label>
                  <input type="text" required value={form.company_name} onChange={(e) => handleFormChange("company_name", e.target.value)} className={inputClass} placeholder="Firma adı" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">İlgili Kişi *</label>
                  <input type="text" required value={form.contact_name} onChange={(e) => handleFormChange("contact_name", e.target.value)} className={inputClass} placeholder="Ad Soyad" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">E-posta</label>
                  <input type="email" value={form.contact_email} onChange={(e) => handleFormChange("contact_email", e.target.value)} className={inputClass} placeholder="ornek@firma.com" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">Telefon</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <span className="absolute left-9 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">+90</span>
                    <input
                      type="tel"
                      value={phoneDisplay}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder="(5xx) xxx xx xx"
                      maxLength={19}
                      className={cn(inputClass, "pl-16")}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">Vergi No</label>
                  <input type="text" value={form.tax_number} onChange={(e) => handleFormChange("tax_number", e.target.value)} className={inputClass} placeholder="10 haneli vergi numarası" maxLength={11} />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">Sektör</label>
                  <select value={form.sector} onChange={(e) => handleFormChange("sector", e.target.value)} className={inputClass}>
                    <option value="">Seçin...</option>
                    {SECTORS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">Müşteri Türü</label>
                  <select value={form.client_type} onChange={(e) => handleFormChange("client_type", e.target.value)} className={inputClass}>
                    {CLIENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <SearchableSelect
                  label="İl"
                  options={iller}
                  value={selectedIlId}
                  valueName={form.city}
                  onChange={(id) => handleCityChange(id)}
                  placeholder="İl seçin..."
                />
                <SearchableSelect
                  label="İlçe"
                  options={ilceler}
                  value={ilceler.find((i) => i.name === form.district)?.id || 0}
                  valueName={form.district}
                  onChange={(id, name) => {
                    const ilce = ilceler.find((i) => i.id === id);
                    if (ilce) handleDistrictChange(ilce.id);
                    else setForm((prev) => ({ ...prev, district: name }));
                  }}
                  placeholder={selectedIlId ? "İlçe seçin..." : "Önce il seçin"}
                  disabled={!selectedIlId}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Notlar</label>
                <textarea value={form.notes} onChange={(e) => handleFormChange("notes", e.target.value)} rows={3} className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent" placeholder="Müşteri hakkında ek bilgiler..." />
              </div>

              {/* Logo & Stamp uploads */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Firma Logosu (opsiyonel)
                  </label>
                  {logoPreview ? (
                    <div className="relative rounded-lg border border-border bg-secondary p-3">
                      <img
                        src={logoPreview}
                        alt="Logo"
                        className="mx-auto h-20 w-auto object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => clearFile("logo")}
                        className="absolute right-2 top-2 rounded-full bg-secondary p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border p-4 transition-colors hover:border-accent/50 hover:bg-secondary/50">
                      <Upload className="h-5 w-5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        JPG, PNG, SVG veya WebP
                      </span>
                      <input
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES}
                        className="hidden"
                        onChange={(e) => {
                          handleFileSelect(e.target.files?.[0] || null, "logo");
                          e.target.value = "";
                        }}
                      />
                    </label>
                  )}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Firma Kaşesi (opsiyonel)
                  </label>
                  {stampPreview ? (
                    <div className="relative rounded-lg border border-border bg-secondary p-3">
                      <img
                        src={stampPreview}
                        alt="Kaşe"
                        className="mx-auto h-20 w-auto object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => clearFile("stamp")}
                        className="absolute right-2 top-2 rounded-full bg-secondary p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border p-4 transition-colors hover:border-accent/50 hover:bg-secondary/50">
                      <Upload className="h-5 w-5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        JPG, PNG, SVG veya WebP
                      </span>
                      <input
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES}
                        className="hidden"
                        onChange={(e) => {
                          handleFileSelect(e.target.files?.[0] || null, "stamp");
                          e.target.value = "";
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
              {/* TODO: Logo ve kaşe dosyaları, müşteri oluşturulduktan sonra R2'ye yüklenecek. Client upload API endpoint'i gerekli. */}

              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                  İptal
                </button>
                <button type="submit" disabled={submitting} className={cn("inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90", submitting && "cursor-not-allowed opacity-50")}>
                  {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
